import React, { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Power,
  PowerOff,
  ShieldAlert,
  AlertTriangle,
  Search,
  RefreshCw,
  Loader2,
  X,
  Lock,
  CheckCircle2,
  Boxes,
  Plug,
  Server,
  Bot,
  History,
} from 'lucide-react';
import clsx from 'clsx';
import api from '../api/client';
import appConfig from '../config';
import { useAuth } from '../context/AuthContext';
import Breadcrumbs from '../components/Breadcrumbs';
import ErrorBoundary from '../components/ErrorBoundary';

/**
 * Traffic Controls console.
 *
 * Exposes four INDEPENDENT kill-switch tiers, one per tab:
 *
 *   • Products — a boolean APIM named value `disable-<ProductName>`.
 *     When true, a product-scoped policy returns HTTP 503 for every API
 *     and subscription under that product.
 *   • APIs — `disable-api-<ApiId>`, read by an API-scoped policy, so a
 *     single misbehaving API can be taken out without darkening everything
 *     else its product publishes.
 *   • MCP servers — `disable-mcp-<ApiId>`, read by a `*-kill-switch` policy
 *     fragment on the MCP server's API. MCP servers are the entries APIM
 *     itself tags `properties.type === 'mcp'` (the portal's "MCP Servers"
 *     blade), whether backed by an API's operations or a native MCP server.
 *   • Agents — `disable-agent-<ApiId>`. Agents are the entries APIM flags
 *     `isAgent === true` (A2A agent APIs), NOT a naming guess.
 *
 * The MCP / agent classification comes straight from APIM's own metadata
 * (returned on api-version 2025-09-01-preview), so every tab mirrors the
 * Azure portal exactly — see killSwitchService.classifyApi().
 *
 * All four tiers write to SEPARATE named-value namespaces server-side, which
 * is why entities sharing a display name across tiers cannot collide.
 *
 * NOTE ON NAMING: this page is "Traffic Controls" to the operator and the
 * backend route is `/api/killswitch`, but the APIM named values use the
 * platform team's `disable-*` convention. The named-value prefix is the one
 * name that is NOT ours to choose — it must match what the APIM policy
 * reads, or we write a value nothing consumes and report a shutdown that
 * never happened. It lives in config.killSwitch.prefix.
 *
 * SAFETY / SECURITY UX:
 *   • Admin-only. Gated in the router AND re-checked here so a stale bundle
 *     can never render the controls (the API is the real enforcement point).
 *   • Disabling requires typing the entity name to confirm plus a written
 *     justification — no single mis-click can take a service down.
 *   • The state the operator saw is sent as `expectedCurrent`, so a
 *     concurrent change by another admin is rejected with 409 instead of
 *     being silently overwritten.
 *   • Data is never cached (staleTime 0) because a stale "live" reading on
 *     a safety control is worse than a slow one.
 */

/**
 * Per-tier configuration. Everything that differs between the Products and
 * APIs tabs lives HERE, and the rendering below is written against it. That
 * keeps one code path for both tiers: a fix to the confirmation flow or the
 * conflict handling can't land on products and silently miss APIs.
 */
const TIERS = {
  product: {
    id: 'product',
    tab: 'products',
    icon: Boxes,
    label: 'Products',
    noun: 'product',
    nounPlural: 'products',
    title: 'Product controls',
    blurb:
      'Disable every API and subscription under a published APIM product. Use this when an entire offering must stop serving traffic.',
    // Impact copy shown in the confirm dialog — deliberately concrete about
    // blast radius, because product and API scope differ enormously.
    impact:
      'All APIs and subscriptions under this product will immediately receive HTTP 503. Consumer applications will fail until the switch is turned off.',
    restoreImpact: 'Traffic to every API under this product will resume.',
    emptyText: 'No published products found on this APIM instance.',
    list: (apimService) => api.getKillSwitches(apimService),
    set: (vars) => api.setKillSwitch(vars),
    idField: 'productId',
  },
  api: {
    id: 'api',
    tab: 'apis',
    icon: Plug,
    label: 'APIs',
    noun: 'API',
    nounPlural: 'APIs',
    title: 'API controls',
    blurb:
      'Disable a single API without affecting the rest of its product. Use this to isolate one failing operation set.',
    impact:
      'Every operation on this API will immediately return HTTP 503, across all products that publish it. Other APIs keep serving traffic.',
    restoreImpact: 'Traffic to this API will resume across all products that publish it.',
    emptyText: 'No APIs found on this APIM instance.',
    list: (apimService) => api.getApiKillSwitches(apimService),
    set: (vars) => api.setApiKillSwitch(vars),
    idField: 'apiId',
  },
  mcp: {
    id: 'mcp',
    tab: 'mcp',
    icon: Server,
    label: 'MCP servers',
    noun: 'MCP server',
    nounPlural: 'MCP servers',
    title: 'MCP server controls',
    blurb:
      'Disable a single MCP server without affecting LLM APIs or products. Use this to cut off a misbehaving tool/context server.',
    impact:
      'Every request to this MCP server will immediately return HTTP 503. Assistants and agents that depend on it will lose that capability until the switch is turned off.',
    restoreImpact: 'Traffic to this MCP server will resume.',
    emptyText: 'No MCP servers found on this APIM instance.',
    list: (apimService) => api.getMcpKillSwitches(apimService),
    set: (vars) => api.setMcpKillSwitch(vars),
    idField: 'apiId',
  },
  agent: {
    id: 'agent',
    tab: 'agents',
    icon: Bot,
    label: 'Agents',
    noun: 'agent',
    nounPlural: 'agents',
    title: 'Agent controls',
    blurb:
      'Disable a single agent API without affecting other APIs, MCP servers, or products. Use this to stop one agent while keeping the platform live.',
    impact:
      'Every request to this agent will immediately return HTTP 503. Callers of this agent will fail until the switch is turned off.',
    restoreImpact: 'Traffic to this agent will resume.',
    emptyText: 'No agents found on this APIM instance.',
    list: (apimService) => api.getAgentKillSwitches(apimService),
    set: (vars) => api.setAgentKillSwitch(vars),
    idField: 'apiId',
  },
};

const TAB_ORDER = ['product', 'api', 'mcp', 'agent'];

/** Map a `?tab=` value back to a tier id, defaulting to products. */
function tierFromTabParam(value) {
  return TAB_ORDER.find((id) => TIERS[id].tab === value) || 'product';
}

/**
 * Split a stored reason into the operator's own words and the audit stamp
 * the backend appends (`"… (set by NAME on DATE)"`).
 *
 * Rendering them as one string buries the ONE piece of information a
 * responder actually needs — why it's down — behind boilerplate that is
 * identical on every row. If the suffix isn't present (a value written by
 * hand in the portal, say) we fall back to showing the text untouched
 * rather than guessing.
 */
function splitReason(reason) {
  const text = String(reason || '').trim();
  if (!text) return { text: '', stamp: '' };
  const match = text.match(/^([\s\S]*?)\s*\((set by [\s\S]+)\)$/);
  return match ? { text: match[1], stamp: match[2] } : { text, stamp: '' };
}

/**
 * The muted second line under an entity's name.
 *
 * For APIs this is the route path, which is real information. For products
 * it's the APIM id — usually just a slugified copy of the display name
 * ("EASP Dev Team" / "EASP-dev-team"), which reads as duplication and adds
 * nothing. Suppress it in exactly that case, but keep it whenever it differs
 * meaningfully, since then it IS the identifier someone needs.
 */
function entitySubtitle(entity) {
  if (entity.path) return `/${String(entity.path).replace(/^\//, '')}`;
  const id = String(entity.entityId || '');
  const name = String(entity.displayName || '');
  if (!name) return id;
  const norm = (s) => s.toLowerCase().replace(/[^a-z0-9]/g, '');
  return norm(id) === norm(name) ? '' : id;
}

// ── Small presentational helpers ─────────────────────────────────────

/**
 * Two-line reason cell: the justification, then the attribution in a muted
 * smaller line. `title` carries the full original so the raw stored value is
 * still recoverable on hover.
 */
function ReasonText({ reason, className }) {
  const { text, stamp } = splitReason(reason);
  if (!text) return <span className="text-gray-400">—</span>;
  return (
    <span className={className} title={reason}>
      <span className="break-words">{text}</span>
      {stamp && <span className="block text-gray-400 mt-0.5 break-words">{stamp}</span>}
    </span>
  );
}

/**
 * Shown when an entity shares its APIM named value with another entity
 * (two published products with the same display name, for example).
 *
 * Toggling such a switch would take down the bystander too while reporting
 * success only for the one named — so the action is disabled and the reason
 * stated plainly. The backend rejects these writes independently; this just
 * explains the greyed-out button instead of leaving it a mystery.
 */
function AmbiguousNote({ entity }) {
  return (
    <span className="inline-flex items-start gap-1.5 text-amber-700">
      <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
      <span className="break-words">
        Shares the named value <code className="break-all">{entity.namedValue}</code> with another{' '}
        entry. Rename one before using this switch.
      </span>
    </span>
  );
}

/**
 * Explains a switch that is set but that no APIM policy reads.
 *
 * The named value exists and says "true", yet nothing consumes it, so
 * traffic is still flowing. This is the one situation where the raw named
 * value would otherwise make the console LIE — hence its own prominent
 * treatment rather than a footnote.
 */
function NotEnforcedNote({ entity, engaged }) {
  return (
    <span className="inline-flex items-start gap-1.5 text-amber-700">
      <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
      <span className="break-words">
        No APIM policy reads <code className="break-all">{entity.namedValue}</code>, so this switch
        {engaged ? ' is NOT blocking traffic.' : ' would not block traffic.'} Add the kill-switch
        policy to this entry in APIM.
      </span>
    </span>
  );
}

/**
 * Three states, not two.
 *
 * "Blocked (503)" is only truthful when a policy actually reads the named
 * value. When the switch is on but unenforced we must NOT show red-and-
 * confident: an operator seeing "Blocked" would stop investigating while the
 * service is still serving. `enforced === null` means we couldn't check
 * (permissions, ARM error), so we fall back to the plain reading rather than
 * inventing an alarm.
 */
function StatusBadge({ enabled, enforced }) {
  if (enabled && enforced === false) {
    return (
      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ring-1 whitespace-nowrap bg-amber-50 text-amber-800 ring-amber-300">
        <AlertTriangle className="w-3 h-3 shrink-0" />
        Set, not enforced
      </span>
    );
  }
  return (
    <span
      className={clsx(
        'inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ring-1 whitespace-nowrap',
        enabled ? 'bg-red-50 text-red-700 ring-red-200' : 'bg-green-50 text-green-700 ring-green-200'
      )}
    >
      <span
        className={clsx('w-1.5 h-1.5 rounded-full', enabled ? 'bg-red-500 animate-pulse' : 'bg-green-500')}
      />
      {enabled ? 'Blocked (503)' : 'Live'}
    </span>
  );
}

function SummaryCard({ icon: Icon, label, value, tone = 'neutral' }) {
  const tones = {
    neutral: 'bg-gray-50 text-gray-600 ring-gray-200',
    danger: 'bg-red-50 text-red-600 ring-red-200',
    success: 'bg-green-50 text-green-600 ring-green-200',
  };
  return (
    <div className="flex items-center gap-3 bg-white rounded-xl border border-gray-200 p-4">
      <div className={clsx('p-2.5 rounded-lg ring-1 shrink-0', tones[tone])}>
        <Icon className="w-5 h-5" />
      </div>
      <div className="min-w-0">
        <p className="text-xs text-gray-500 truncate">{label}</p>
        <p className="text-xl font-bold text-sce-dark">{value}</p>
      </div>
    </div>
  );
}

// ── Audit history modal ──────────────────────────────────────────────

/** Format an ISO instant as a compact, locale-aware UTC-explicit label. */
function formatAuditTime(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
}

/**
 * Read-only timeline of every kill-switch flip for a single entity, sourced
 * from the durable Cosmos audit store (outlives App Insights' 90-day
 * retention). Purely informational — no controls — so it needs only
 * Escape-to-close and background scroll-lock, mirroring ConfirmDialog's
 * modal chrome for visual consistency.
 *
 * The backend answers GET /killswitch/audit/:tier/:entityId with HTTP 503
 * when Cosmos is not configured; apiFetch turns that into a thrown error
 * carrying `.status === 503`, which we surface as a calm "not configured"
 * notice rather than a red failure.
 */
function AuditHistoryModal({ tier, entity, onClose }) {
  const dialogRef = useRef(null);

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['killswitch-audit', tier.id, entity.entityId],
    queryFn: () => api.getKillSwitchAuditFor(tier.id, entity.entityId),
    // History is append-only and read on demand — no need to poll, but always
    // fetch fresh when the modal opens so a just-made change shows up.
    staleTime: 0,
    refetchOnWindowFocus: false,
    retry: false,
  });

  const notConfigured = isError && error?.status === 503;
  const records = data?.data || [];

  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = previousOverflow;
    };
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/50 p-0 sm:p-4"
      role="presentation"
      onMouseDown={(e) => {
        // Click on the backdrop (not the panel) closes — the panel stops
        // propagation below.
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="tc-history-title"
        onMouseDown={(e) => e.stopPropagation()}
        className="bg-white w-full sm:max-w-2xl rounded-t-2xl sm:rounded-2xl shadow-xl max-h-[92dvh] sm:max-h-[90dvh] flex flex-col overflow-hidden"
      >
        <div className="flex items-start gap-3 p-4 sm:p-5 border-b border-gray-200 shrink-0">
          <div className="p-2 rounded-lg shrink-0 bg-sce-green/10 text-sce-green">
            <History className="w-5 h-5" />
          </div>
          <div className="min-w-0 flex-1">
            <h2 id="tc-history-title" className="text-lg font-bold text-sce-dark">
              Audit history
            </h2>
            <p className="text-sm text-gray-500 break-words">
              {entity.displayName || entity.entityId} · {tier.noun}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-1 text-gray-400 hover:text-gray-600"
            aria-label="Close audit history"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-4 sm:p-5 overflow-y-auto flex-1">
          {isLoading && (
            <div className="flex items-center justify-center gap-2 py-10 text-gray-500">
              <Loader2 className="w-5 h-5 animate-spin" />
              <span className="text-sm">Loading history…</span>
            </div>
          )}

          {notConfigured && (
            <div
              role="status"
              className="flex gap-2.5 p-3 rounded-lg text-sm ring-1 bg-amber-50 text-amber-900 ring-amber-300"
            >
              <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
              <div className="min-w-0">
                <p className="font-semibold">Durable audit store not configured.</p>
                <p className="mt-1">
                  Persistent history requires a Cosmos DB connection
                  (<code>COSMOS_ENDPOINT</code> or <code>COSMOS_CONNECTION_STRING</code>).
                  Recent changes are still captured in Application Insights for 90 days.
                </p>
              </div>
            </div>
          )}

          {isError && !notConfigured && (
            <div
              role="alert"
              className="flex gap-2.5 p-3 rounded-lg text-sm ring-1 bg-red-50 text-red-800 ring-red-200"
            >
              <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
              <p>Couldn’t load audit history: {error?.message || 'unknown error'}.</p>
            </div>
          )}

          {!isLoading && !isError && records.length === 0 && (
            <div className="text-center py-10 text-gray-500">
              <History className="w-8 h-8 mx-auto mb-2 text-gray-300" />
              <p className="text-sm">No recorded changes for this {tier.noun} yet.</p>
            </div>
          )}

          {!isLoading && !isError && records.length > 0 && (
            <ol className="relative space-y-4">
              {records.map((r) => {
                const engaged = r.action === 'ENGAGE';
                const failed = r.outcome === 'error';
                const conflict = r.outcome === 'conflict';
                return (
                  <li
                    key={r.id}
                    className="relative pl-6 border-l-2 border-gray-200 last:border-l-transparent"
                  >
                    <span
                      className={clsx(
                        'absolute -left-[7px] top-1 w-3 h-3 rounded-full ring-2 ring-white',
                        failed || conflict
                          ? 'bg-amber-500'
                          : engaged
                          ? 'bg-red-500'
                          : 'bg-green-500'
                      )}
                      aria-hidden="true"
                    />
                    <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                      <span
                        className={clsx(
                          'inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold ring-1',
                          engaged
                            ? 'bg-red-50 text-red-700 ring-red-200'
                            : 'bg-green-50 text-green-700 ring-green-200'
                        )}
                      >
                        {engaged ? <ShieldAlert className="w-3 h-3" /> : <CheckCircle2 className="w-3 h-3" />}
                        {engaged ? 'Disabled' : 'Restored'}
                      </span>
                      {(failed || conflict) && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-amber-50 text-amber-800 ring-1 ring-amber-200">
                          <AlertTriangle className="w-3 h-3" />
                          {conflict ? 'Conflict — not applied' : 'Failed'}
                        </span>
                      )}
                      <span className="text-xs text-gray-500">{formatAuditTime(r.timestamp)}</span>
                    </div>

                    <p className="mt-1.5 text-sm text-sce-dark">
                      <span className="font-medium">
                        {r.actor?.displayName || r.actor?.name || r.actor?.email || 'Unknown operator'}
                      </span>
                      {/* Only append the bare email when displayName is just
                          the name (no email already embedded) — avoids
                          "Jane (jane@x) (jane@x)". */}
                      {!r.actor?.displayName && r.actor?.email && r.actor?.name && (
                        <span className="text-gray-500"> ({r.actor.email})</span>
                      )}
                    </p>

                    {r.reason && (
                      <p className="mt-1 text-sm text-gray-600 break-words">
                        <span className="text-gray-400">Reason: </span>
                        {r.reason}
                      </p>
                    )}

                    {failed && r.error?.message && (
                      <p className="mt-1 text-xs text-amber-700 break-words">
                        {r.error.message}
                      </p>
                    )}
                  </li>
                );
              })}
            </ol>
          )}
        </div>

        <div className="p-4 sm:p-5 border-t border-gray-200 shrink-0 flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Confirmation dialog ──────────────────────────────────────────────

/**
 * Modal that collects the MANDATORY justification and, when disabling, a
 * typed confirmation of the entity name. Focus-trapped, Escape-to-close and
 * labelled for assistive tech.
 */
function ConfirmDialog({ tier, entity, nextEnabled, limits, onCancel, onConfirm, isPending, error }) {
  const [reason, setReason] = useState('');
  const [typed, setTyped] = useState('');
  const inputRef = useRef(null);
  const dialogRef = useRef(null);

  const engaging = nextEnabled;
  const min = limits.reasonMinLength ?? 10;
  const max = limits.reasonMaxLength ?? 500;

  // Engaging a switch that no policy reads would be a no-op reported as an
  // outage. The backend rejects it with 409; blocking it here too means the
  // operator learns why BEFORE typing a reason and a confirmation. Restoring
  // stays allowed so an inert switch can always be cleared.
  const unenforced = engaging && entity.enforced === false;

  // Disabling is destructive, so require the operator to retype the NAME they
  // see on screen, not the internal id. Restoring only needs a justification.
  const confirmToken = entity.displayName || entity.entityId;
  const typedOk = !engaging || typed.trim() === confirmToken;
  const reasonOk = reason.trim().length >= min && reason.trim().length <= max;
  const canSubmit = typedOk && reasonOk && !isPending && !unenforced;

  useEffect(() => {
    inputRef.current?.focus();
    // Lock background scrolling while the dialog is open. Without this, on
    // touch devices a scroll gesture starting over the backdrop scrolls the
    // list underneath, so the operator loses their place mid-confirmation.
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e) => {
      if (e.key === 'Escape' && !isPending) onCancel();
      if (e.key === 'Tab' && dialogRef.current) {
        const focusable = dialogRef.current.querySelectorAll(
          'button:not([disabled]), input, textarea, [href]'
        );
        if (!focusable.length) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = previousOverflow;
    };
  }, [onCancel, isPending]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/50 p-0 sm:p-4"
      role="presentation"
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="tc-dialog-title"
        // `dvh` (dynamic viewport height) rather than `vh`: on mobile the
        // on-screen keyboard shrinks the visual viewport, and `vh` would keep
        // sizing against the FULL screen height — pushing the confirm buttons
        // out of reach exactly when the operator is typing the reason.
        className="bg-white w-full sm:max-w-lg rounded-t-2xl sm:rounded-2xl shadow-xl max-h-[92dvh] sm:max-h-[90dvh] flex flex-col overflow-hidden"
      >
        <div className="flex items-start gap-3 p-4 sm:p-5 border-b border-gray-200 shrink-0">
          <div
            className={clsx(
              'p-2 rounded-lg shrink-0',
              engaging ? 'bg-red-50 text-red-600' : 'bg-green-50 text-green-600'
            )}
          >
            {engaging ? <ShieldAlert className="w-5 h-5" /> : <CheckCircle2 className="w-5 h-5" />}
          </div>
          <div className="min-w-0 flex-1">
            <h2 id="tc-dialog-title" className="text-lg font-bold text-sce-dark">
              {engaging ? `Disable ${tier.noun}` : 'Restore traffic'}
            </h2>
            <p className="text-sm text-gray-500 break-words">{confirmToken}</p>
          </div>
          <button
            onClick={onCancel}
            disabled={isPending}
            className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-50"
            aria-label="Close dialog"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Only the body scrolls, so the confirm/cancel actions stay pinned
            and reachable no matter how long the reason text grows. */}
        <div className="p-4 sm:p-5 space-y-4 overflow-y-auto flex-1">
          {/* When nothing reads the named value, the impact copy below would
              be a promise we cannot keep — so replace it outright rather than
              stacking a caveat under it. */}
          {unenforced ? (
            <div
              role="alert"
              className="flex gap-2.5 p-3 rounded-lg text-sm ring-1 bg-amber-50 text-amber-900 ring-amber-300"
            >
              <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
              <div className="min-w-0">
                <p className="font-semibold">This switch is not wired up.</p>
                <p className="mt-1">
                  No APIM policy reads{' '}
                  <code className="break-all">{entity.namedValue}</code>, so setting it would not
                  block any traffic — while this console reported an outage. Add the kill-switch
                  policy to this {tier.noun} in APIM first.
                </p>
              </div>
            </div>
          ) : (
            <div
              className={clsx(
                'flex gap-2.5 p-3 rounded-lg text-sm ring-1',
                engaging ? 'bg-red-50 text-red-800 ring-red-200' : 'bg-amber-50 text-amber-800 ring-amber-200'
              )}
            >
              <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
              <p>
                {engaging
                  ? tier.impact
                  : `${tier.restoreImpact} Confirm the underlying issue is resolved before continuing.`}
              </p>
            </div>
          )}

          {/* Mandatory justification — stored in APIM as the `-reason` named
              value and written to the audit record. */}
          <div>
            <label htmlFor="tc-reason" className="block text-sm font-medium text-gray-700 mb-1">
              Reason <span className="text-red-600">*</span>
            </label>
            <textarea
              id="tc-reason"
              ref={engaging ? undefined : inputRef}
              value={reason}
              onChange={(e) => setReason(e.target.value.slice(0, max))}
              rows={3}
              maxLength={max}
              placeholder="e.g. INC0042318 — upstream model returning malformed responses"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-sce-green/40 focus:border-sce-green resize-y"
            />
            <div className="flex justify-between mt-1 text-xs gap-2">
              {/* Warns that the stored text is not verbatim — the backend
                  appends "(set by … on …)". An operator who expects their
                  words back unchanged would otherwise be surprised reading
                  the value in the Azure portal. The named value it lands in
                  is NOT spelled out here: it's implementation detail, and
                  naming it made this line compete with the switch reference
                  below for the operator's attention. */}
              <span className={reasonOk ? 'text-gray-400' : 'text-amber-600'}>
                Minimum {min} characters. Stored in APIM with your name and the
                time appended, and recorded in the audit log.
              </span>
              <span className="text-gray-400 tabular-nums shrink-0">
                {reason.trim().length}/{max}
              </span>
            </div>
          </div>

          {engaging && (
            <div>
              <label htmlFor="tc-confirm" className="block text-sm font-medium text-gray-700 mb-1">
                Type{' '}
                <code className="px-1 py-0.5 bg-gray-100 rounded text-xs break-all">{confirmToken}</code>{' '}
                to confirm
              </label>
              <input
                id="tc-confirm"
                ref={inputRef}
                type="text"
                value={typed}
                onChange={(e) => setTyped(e.target.value)}
                autoComplete="off"
                spellCheck={false}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm font-mono focus:outline-none focus:ring-2 focus:ring-red-400/40 focus:border-red-400"
              />
            </div>
          )}

          {/* The exact named value and the value being written, so an
              operator can cross-check it against the scoped APIM policy
              before committing. This is the one detail worth surfacing: a
              prefix mismatch here means the write succeeds and blocks
              nothing. The `-reason` companion is deliberately NOT listed —
              it's derived from this name, nobody cross-checks it against a
              policy, and showing it duplicated the line above. */}
          {entity.namedValue && (
            <p className="text-xs text-gray-400 break-all">
              Sets <code className="text-gray-500">{entity.namedValue}</code> to{' '}
              <code className="text-gray-500">{String(nextEnabled)}</code>
            </p>
          )}

          {error && (
            <div role="alert" className="p-3 rounded-lg bg-red-50 text-red-700 text-sm ring-1 ring-red-200">
              {error}
            </div>
          )}
        </div>

        <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2 p-4 sm:p-5 border-t border-gray-200 shrink-0 bg-white">
          <button
            onClick={onCancel}
            disabled={isPending}
            className="px-4 py-2.5 rounded-lg text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={() => onConfirm(reason.trim())}
            disabled={!canSubmit}
            className={clsx(
              'inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm font-semibold text-white transition disabled:opacity-40 disabled:cursor-not-allowed',
              engaging ? 'bg-red-600 hover:bg-red-700' : 'bg-sce-green hover:opacity-90'
            )}
          >
            {isPending ? (
              <Loader2 className="w-4 h-4 animate-spin shrink-0" />
            ) : engaging ? (
              <PowerOff className="w-4 h-4 shrink-0" />
            ) : (
              <Power className="w-4 h-4 shrink-0" />
            )}
            <span className="truncate">
              {isPending ? 'Applying…' : engaging ? `Disable ${tier.noun}` : 'Restore traffic'}
            </span>
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Tier panel (one tab's worth of content) ──────────────────────────

/**
 * Renders the list, summary and confirmation flow for ONE tier.
 *
 * Keyed by tier id at the call site so switching tabs remounts it — that
 * resets search text and any half-filled dialog, which is the safe default
 * for a control surface (a reason typed for a product must never be carried
 * over and submitted against an API).
 */
function TierPanel({ tier, apimService, onToast }) {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [pending, setPending] = useState(null); // { entity, nextEnabled }
  const [dialogError, setDialogError] = useState('');
  const [historyEntity, setHistoryEntity] = useState(null); // entity whose audit trail is open

  const statesQuery = useQuery({
    queryKey: ['killswitches', tier.id, apimService],
    queryFn: () => tier.list(apimService || undefined),
    // Safety control: never serve a stale reading, and re-check periodically
    // so another admin's change appears without a manual refresh.
    staleTime: 0,
    refetchInterval: 30000,
    refetchOnWindowFocus: true,
    retry: 1,
  });

  const mutation = useMutation({
    mutationFn: ({ entityId, enabled, reason, expectedCurrent }) =>
      tier.set({
        [tier.idField]: entityId,
        enabled,
        reason,
        apimService: apimService || undefined,
        expectedCurrent,
      }),
    onSuccess: (res) => {
      const name = pending?.entity?.displayName || res[tier.idField] || res.entityId;
      setPending(null);
      setDialogError('');
      onToast({
        type: res.current.enabled ? 'danger' : 'success',
        message: res.current.enabled
          ? `${tier.noun} DISABLED: ${name} — traffic now returns 503.`
          : `Traffic RESTORED for ${name}.`,
      });
      queryClient.invalidateQueries({ queryKey: ['killswitches', tier.id] });
    },
    onError: (err) => {
      setDialogError(err.message || 'The change could not be applied.');
      // A 409 means someone else moved the switch — pull the truth back in.
      if (err.status === 409) queryClient.invalidateQueries({ queryKey: ['killswitches', tier.id] });
    },
  });

  const entities = useMemo(
    // Normalise the id field so the rest of this component is tier-agnostic.
    () => (statesQuery.data?.data || []).map((e) => ({ ...e, entityId: e.entityId ?? e[tier.idField] })),
    [statesQuery.data, tier.idField]
  );
  const writesEnabled = statesQuery.data?.writesEnabled !== false;
  const limits = {
    reasonMinLength: statesQuery.data?.reasonMinLength,
    reasonMaxLength: statesQuery.data?.reasonMaxLength,
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const rows = q
      ? entities.filter(
        (e) =>
          String(e.entityId).toLowerCase().includes(q) ||
          (e.displayName || '').toLowerCase().includes(q) ||
          (e.path || '').toLowerCase().includes(q)
      )
      : entities;
    // Surface rows needing attention first: genuinely-blocked entries, then
    // broken switches (set-but-inert, or unusable because no policy reads
    // them), then everything healthy.
    const rank = (e) => {
      if (e.enabled && e.enforced !== false) return 0;
      if (e.enforced === false) return 1;
      return 2;
    };
    return [...rows].sort((a, b) => {
      const d = rank(a) - rank(b);
      if (d !== 0) return d;
      return (a.displayName || a.entityId).localeCompare(b.displayName || b.entityId);
    });
  }, [entities, search]);

  // Split the "switch is on" rows by whether a policy actually enforces
  // them. Counting an inert switch as an outage would overstate the incident;
  // counting it as healthy would hide a broken control. Both need their own
  // number.
  const blocked = useMemo(
    () => entities.filter((e) => e.enabled && e.enforced !== false),
    [entities]
  );
  const blockedCount = blocked.length;
  const unenforced = useMemo(() => entities.filter((e) => e.enforced === false), [entities]);
  const unenforcedEngaged = useMemo(() => unenforced.filter((e) => e.enabled), [unenforced]);

  const openDialog = useCallback((entity, nextEnabled) => {
    setDialogError('');
    setPending({ entity, nextEnabled });
  }, []);

  const confirm = useCallback(
    (reason) => {
      if (!pending) return;
      mutation.mutate({
        entityId: pending.entity.entityId,
        enabled: pending.nextEnabled,
        reason,
        // The posture the operator actually saw, for server-side conflict detection.
        expectedCurrent: pending.entity.enabled,
      });
    },
    [pending, mutation]
  );

  const refreshing = statesQuery.isFetching;

  return (
    <div className="space-y-5">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <p className="text-sm text-gray-500 max-w-3xl">{tier.blurb}</p>
        <button
          onClick={() => statesQuery.refetch()}
          disabled={refreshing}
          title={`Refresh ${tier.noun} state`}
          aria-label={`Refresh ${tier.noun} state`}
          className="inline-flex items-center justify-center gap-2 px-3 py-2 text-sm font-medium rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50 shrink-0 self-start"
        >
          <RefreshCw className={clsx('w-4 h-4 shrink-0', refreshing && 'animate-spin')} />
          <span className="hidden sm:inline">Refresh</span>
        </button>
      </div>

      {/* Summary — two cards, not three. "Total" was derivable (live +
          blocked) and earned no space of its own; the counts that matter
          during an incident are how much is down and how much is still up. */}
      <div className="grid grid-cols-2 gap-3">
        <SummaryCard
          icon={PowerOff}
          label="Blocked (returning 503)"
          value={blockedCount}
          tone={blockedCount ? 'danger' : 'neutral'}
        />
        <SummaryCard icon={Power} label="Live" value={entities.length - blockedCount} tone="success" />
      </div>

      {/* Broken-control banner. Distinct from the outage banner below and
          deliberately ABOVE it: a switch that doesn't work is a standing
          safety defect, and if one is currently ENGAGED the operator may
          believe an outage is contained when traffic is still flowing. */}
      {unenforced.length > 0 && (
        <div role="alert" className="p-3 sm:p-4 rounded-xl bg-amber-50 ring-1 ring-amber-300 text-amber-900">
          <div className="flex gap-2.5">
            <AlertTriangle className="w-5 h-5 shrink-0 mt-0.5 text-amber-600" />
            <div className="text-sm min-w-0 flex-1">
              <p className="font-semibold">
                {unenforcedEngaged.length > 0
                  ? `${unenforcedEngaged.length} switch${unenforcedEngaged.length === 1 ? ' is' : 'es are'} set but NOT blocking traffic`
                  : `${unenforced.length} ${unenforced.length === 1 ? tier.noun : tier.nounPlural} cannot be disabled`}
              </p>
              <p className="mt-1 break-words">
                No APIM policy reads the kill-switch named value for:{' '}
                {unenforced.map((e) => e.displayName || e.entityId).join(', ')}. Until the policy is
                added, setting the switch has no effect on traffic.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* ACTIVE OUTAGE BANNER — entries whose killswitch named value is
          ALREADY `true` (disabled outside this app, or in an earlier
          session). Surfaced above everything else so an operator opening the
          page can never miss that something is currently returning 503.

          It names the affected entities but deliberately OMITS their reasons:
          the table below is sorted blocked-first and shows each reason in
          full, so repeating them here produced the same paragraph twice on
          screen and pushed the actual controls below the fold. */}
      {blockedCount > 0 && (
        <div role="alert" className="p-3 sm:p-4 rounded-xl bg-red-50 ring-1 ring-red-200 text-red-800">
          <div className="flex gap-2.5">
            <ShieldAlert className="w-5 h-5 shrink-0 mt-0.5 text-red-600" />
            <p className="text-sm min-w-0 flex-1">
              <span className="font-semibold">
                Returning HTTP 503 to all consumers:{' '}
              </span>
              <span className="break-words">
                {blocked.map((e) => e.displayName || e.entityId).join(', ')}
              </span>
            </p>
          </div>
        </div>
      )}

      {!writesEnabled && (
        <div className="flex gap-2.5 p-3 rounded-lg bg-amber-50 text-amber-800 text-sm ring-1 ring-amber-200">
          <Lock className="w-4 h-4 shrink-0 mt-0.5" />
          <p>
            Changes are disabled by configuration (<code>KILLSWITCH_WRITES_ENABLED=false</code>). The
            view below is read-only.
          </p>
        </div>
      )}

      {/* `min-w-0` lets the search box actually shrink inside the flex row
          instead of forcing horizontal overflow at narrow widths. */}
      <div className="relative min-w-0">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
        <input
          type="search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={`Search ${tier.nounPlural}…`}
          aria-label={`Search ${tier.nounPlural}`}
          className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-sce-green/40 focus:border-sce-green"
        />
      </div>

      {statesQuery.isLoading && (
        <div className="flex items-center justify-center py-16 text-gray-500 gap-2">
          <Loader2 className="w-5 h-5 animate-spin" />
          Loading {tier.noun} state…
        </div>
      )}

      {statesQuery.isError && (
        <div role="alert" className="p-4 rounded-lg bg-red-50 text-red-700 ring-1 ring-red-200 text-sm">
          {statesQuery.error?.message || `Failed to load ${tier.noun} state.`}
        </div>
      )}

      {!statesQuery.isLoading && !statesQuery.isError && filtered.length === 0 && (
        <p className="py-16 text-center text-gray-500 text-sm">
          {/* Distinguish "your search matched nothing" from "this APIM
              instance genuinely exposes none" — otherwise an operator sees an
              empty list and assumes the filter is at fault. */}
          {entities.length === 0 ? tier.emptyText : `No ${tier.nounPlural} match your search.`}
        </p>
      )}

      {filtered.length > 0 && (
        <>
          {/* Mobile / tablet cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 lg:hidden">
            {filtered.map((e) => (
              <div
                key={e.entityId}
                className={clsx(
                  'bg-white rounded-xl border p-4 space-y-3',
                  e.enabled ? 'border-red-200 ring-1 ring-red-100' : 'border-gray-200'
                )}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <p className="font-semibold text-sce-dark break-words">
                      {e.displayName || e.entityId}
                    </p>
                    {entitySubtitle(e) && (
                      <p className="text-xs text-gray-500 font-mono break-all">{entitySubtitle(e)}</p>
                    )}
                  </div>
                  <StatusBadge enabled={e.enabled} enforced={e.enforced} />
                </div>
                {e.enabled && e.reason && (
                  <div className="text-xs text-gray-600 bg-gray-50 rounded-lg p-2">
                    <ReasonText reason={e.reason} />
                  </div>
                )}
                {e.ambiguous && <AmbiguousNote entity={e} />}
                {!e.ambiguous && e.enforced === false && (
                  <div className="text-xs">
                    <NotEnforcedNote entity={e} engaged={e.enabled} />
                  </div>
                )}
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => openDialog(e, !e.enabled)}
                    disabled={!writesEnabled || e.ambiguous || (!e.enabled && e.enforced === false)}
                    className={clsx(
                      'flex-1 inline-flex items-center justify-center gap-2 px-3 py-2.5 rounded-lg text-sm font-semibold text-white disabled:opacity-40 disabled:cursor-not-allowed',
                      e.enabled ? 'bg-sce-green hover:opacity-90' : 'bg-red-600 hover:bg-red-700'
                    )}
                  >
                    {e.enabled ? (
                      <Power className="w-4 h-4 shrink-0" />
                    ) : (
                      <PowerOff className="w-4 h-4 shrink-0" />
                    )}
                    <span className="truncate">
                      {e.enabled ? 'Restore traffic' : `Disable ${tier.noun}`}
                    </span>
                  </button>
                  <button
                    onClick={() => setHistoryEntity(e)}
                    title={`View change history for ${e.displayName || e.entityId}`}
                    aria-label={`View change history for ${e.displayName || e.entityId}`}
                    className="inline-flex items-center justify-center px-3 py-2.5 rounded-lg text-sm font-medium border border-gray-300 text-gray-700 hover:bg-gray-50 shrink-0"
                  >
                    <History className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>

          {/* Desktop table. `overflow-x-auto` matters because the sidebar
              becomes in-flow at the same lg breakpoint the table appears,
              so the content box is at its tightest right here. */}
          <div className="hidden lg:block bg-white rounded-xl border border-gray-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm min-w-[720px]">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr className="text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                    <th scope="col" className="px-4 py-3">{tier.noun}</th>
                    <th scope="col" className="px-4 py-3 w-40">Status</th>
                    <th scope="col" className="px-4 py-3">Reason</th>
                    <th scope="col" className="px-4 py-3 w-52 text-right">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {filtered.map((e) => (
                    <tr key={e.entityId} className={clsx(e.enabled && 'bg-red-50/40')}>
                      <td className="px-4 py-3">
                        <p className="font-medium text-sce-dark break-words">
                          {e.displayName || e.entityId}
                        </p>
                        {entitySubtitle(e) && (
                          <p className="text-xs text-gray-500 font-mono break-all">{entitySubtitle(e)}</p>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <StatusBadge enabled={e.enabled} enforced={e.enforced} />
                      </td>
                      <td className="px-4 py-3 text-xs text-gray-600 max-w-md">
                        {e.ambiguous ? (
                          <AmbiguousNote entity={e} />
                        ) : e.enforced === false ? (
                          // Outranks the reason text: an unenforced switch
                          // means the stated reason describes an outage that
                          // isn't actually happening.
                          <NotEnforcedNote entity={e} engaged={e.enabled} />
                        ) : e.enabled ? (
                          <ReasonText reason={e.reason} />
                        ) : (
                          <span className="text-gray-400">—</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="inline-flex items-center gap-2">
                          <button
                            onClick={() => openDialog(e, !e.enabled)}
                            disabled={!writesEnabled || e.ambiguous || (!e.enabled && e.enforced === false)}
                            className={clsx(
                              'inline-flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-semibold text-white disabled:opacity-40 disabled:cursor-not-allowed whitespace-nowrap',
                              e.enabled ? 'bg-sce-green hover:opacity-90' : 'bg-red-600 hover:bg-red-700'
                            )}
                          >
                            {e.enabled ? (
                              <Power className="w-4 h-4 shrink-0" />
                            ) : (
                              <PowerOff className="w-4 h-4 shrink-0" />
                            )}
                            {e.enabled ? 'Restore' : 'Disable'}
                          </button>
                          <button
                            onClick={() => setHistoryEntity(e)}
                            title={`View change history for ${e.displayName || e.entityId}`}
                            aria-label={`View change history for ${e.displayName || e.entityId}`}
                            className="inline-flex items-center justify-center px-2.5 py-2 rounded-lg text-sm font-medium border border-gray-300 text-gray-700 hover:bg-gray-50"
                          >
                            <History className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {pending && (
        <ConfirmDialog
          tier={tier}
          entity={pending.entity}
          nextEnabled={pending.nextEnabled}
          limits={limits}
          isPending={mutation.isPending}
          error={dialogError}
          onCancel={() => {
            if (mutation.isPending) return;
            setPending(null);
            setDialogError('');
          }}
          onConfirm={confirm}
        />
      )}

      {historyEntity && (
        <AuditHistoryModal
          tier={tier}
          entity={historyEntity}
          onClose={() => setHistoryEntity(null)}
        />
      )}
    </div>
  );
}

// ── Page ─────────────────────────────────────────────────────────────

function TrafficControlsContent() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [apimService, setApimService] = useState('');
  const [toast, setToast] = useState(null);

  // The active tab lives in the URL so an operator can paste a link that
  // opens directly on the tier they're talking about — during an incident
  // that removes a round of "which tab?" confusion.
  const activeTier = tierFromTabParam(searchParams.get('tab'));

  const selectTier = useCallback(
    (tierId) => {
      // Updater form (not a captured `searchParams`) so we merge against the
      // CURRENT query string. Any other param already on the URL survives the
      // tab switch instead of being clobbered by a stale snapshot.
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set('tab', TIERS[tierId].tab);
          return next;
        },
        // `replace` so tab switching doesn't bury the previous page under a
        // pile of history entries the back button has to chew through.
        { replace: true }
      );
    },
    [setSearchParams]
  );

  const instancesQuery = useQuery({
    queryKey: ['apim-instances'],
    queryFn: () => api.getApimInstances(),
    staleTime: 15 * 60 * 1000,
  });

  // Preselect the env-configured default APIM instance once loaded.
  useEffect(() => {
    if (!apimService && instancesQuery.data?.default) {
      setApimService(instancesQuery.data.default);
    }
  }, [instancesQuery.data, apimService]);

  useEffect(() => {
    if (!toast) return undefined;
    const t = setTimeout(() => setToast(null), 6000);
    return () => clearTimeout(t);
  }, [toast]);

  const instances = instancesQuery.data?.data || [];

  // Roving-tabindex keyboard support: arrow keys move between tabs, which is
  // what the WAI-ARIA tabs pattern requires for keyboard users.
  const onTabKeyDown = useCallback(
    (e) => {
      const idx = TAB_ORDER.indexOf(activeTier);
      if (e.key === 'ArrowRight') selectTier(TAB_ORDER[(idx + 1) % TAB_ORDER.length]);
      else if (e.key === 'ArrowLeft') selectTier(TAB_ORDER[(idx - 1 + TAB_ORDER.length) % TAB_ORDER.length]);
      else return;
      e.preventDefault();
    },
    [activeTier, selectTier]
  );

  return (
    <div className="space-y-5">
      <Breadcrumbs items={[{ label: 'Traffic Controls' }]} />

      {/* Header — stacks vertically on phones so the title never competes
          with the instance selector for horizontal space. */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0">
          <h1 className="text-xl sm:text-2xl font-bold text-sce-dark flex items-center gap-2">
            <ShieldAlert className="w-5 h-5 sm:w-6 sm:h-6 text-red-600 shrink-0" />
            <span className="break-words">Traffic Controls</span>
          </h1>
          {/* Deliberately does NOT restate what the page blocks — the active
              tab's blurb covers scope. This line carries only the guarantee
              that applies to BOTH tiers, so the two never say the same thing. */}
          <p className="text-sm text-gray-500 mt-1">
            Every change requires a reason and is recorded in the audit log.
          </p>
        </div>
        {instances.length > 1 && (
          <select
            value={apimService}
            onChange={(e) => setApimService(e.target.value)}
            aria-label="APIM instance"
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-sce-green/40 w-full sm:w-64 shrink-0"
          >
            {instances.map((i) => (
              <option key={i.name} value={i.name}>
                {i.name}
                {i.isDefault ? ' (default)' : ''}
              </option>
            ))}
          </select>
        )}
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <div role="tablist" aria-label="Traffic control scope" className="flex gap-1 -mb-px">
          {TAB_ORDER.map((id) => {
            const t = TIERS[id];
            const Icon = t.icon;
            const active = id === activeTier;
            return (
              <button
                key={id}
                role="tab"
                id={`tc-tab-${t.tab}`}
                aria-selected={active}
                aria-controls={`tc-panel-${t.tab}`}
                tabIndex={active ? 0 : -1}
                onClick={() => selectTier(id)}
                onKeyDown={onTabKeyDown}
                className={clsx(
                  'inline-flex items-center gap-2 px-4 py-2.5 text-sm font-semibold border-b-2 transition whitespace-nowrap',
                  active
                    ? 'border-sce-green text-sce-dark'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                )}
              >
                <Icon className="w-4 h-4 shrink-0" />
                {t.label}
              </button>
            );
          })}
        </div>
      </div>

      <div
        role="tabpanel"
        id={`tc-panel-${TIERS[activeTier].tab}`}
        aria-labelledby={`tc-tab-${TIERS[activeTier].tab}`}
      >
        {/* `key` forces a remount on tab change so search text and any
            half-filled dialog are discarded — a reason typed for a product
            must never be carried over and submitted against an API. */}
        <TierPanel
          key={activeTier}
          tier={TIERS[activeTier]}
          apimService={apimService}
          onToast={setToast}
        />
      </div>

      {/* Toast — aria-live so screen readers announce the outcome. */}
      {toast && (
        <div
          role="status"
          aria-live="polite"
          className={clsx(
            'fixed bottom-4 inset-x-4 sm:inset-x-auto sm:right-6 sm:max-w-sm z-50 p-4 rounded-xl shadow-lg text-sm ring-1 break-words',
            toast.type === 'danger'
              ? 'bg-red-600 text-white ring-red-700'
              : 'bg-sce-green text-white ring-green-800'
          )}
        >
          {toast.message}
        </div>
      )}
    </div>
  );
}

export default function TrafficControls() {
  const { isAdmin } = useAuth();

  // Defence in depth. The route already gates on the admin role and the API
  // enforces it server-side; this guarantees the controls never render even
  // if the page is reached some other way.
  if (!isAdmin) {
    return (
      <div className="flex flex-col items-center justify-center py-24 text-center">
        <Lock className="w-10 h-10 text-gray-400 mb-3" />
        <h1 className="text-lg font-bold text-sce-dark">Access denied</h1>
        <p className="text-sm text-gray-500 mt-1">
          Traffic Controls require the <code>{appConfig.rbac.adminRole}</code> role.
        </p>
      </div>
    );
  }

  return (
    <ErrorBoundary>
      <TrafficControlsContent />
    </ErrorBoundary>
  );
}
